import { useEffect, useRef, useState } from "react";
import { animate, useInView, useReducedMotion } from "framer-motion";

interface CountUpProps {
  /** Final numeric value to land on. */
  value: number;
  /** Optional text before the number, e.g. "$". */
  prefix?: string;
  /** Optional text after the number, e.g. "+", "%", "s". */
  suffix?: string;
  /** Decimal places to keep (e.g. 1 for "2.4"). */
  decimals?: number;
  duration?: number;
  className?: string;
}

/**
 * Animates a number counting up from 0 to `value` once it scrolls into view.
 * Local copy of the dashboard feature's CountUp so this feature stays
 * self-contained under src/features/automations/**.
 */
export function CountUp({
  value,
  prefix = "",
  suffix = "",
  decimals = 0,
  duration = 1,
  className,
}: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const prefersReducedMotion = useReducedMotion();
  const [display, setDisplay] = useState(prefersReducedMotion ? value : 0);

  useEffect(() => {
    if (!isInView) return;
    if (prefersReducedMotion) {
      setDisplay(value);
      return;
    }
    const controls = animate(0, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (latest) => setDisplay(latest),
    });
    return () => controls.stop();
  }, [isInView, value, duration, prefersReducedMotion]);

  const formatted = decimals > 0
    ? display.toFixed(decimals)
    : Math.round(display).toLocaleString();

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
